import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { UserService } from '../core/services/user.service';
import { AppUrls } from '../routing/app-urls';
import { SignInComponent } from './sign-in/sign-in.component';
import { SignUpComponent } from './sign-up/sign-up.component';
import { ClientLoggingService } from '../core/services/client-logging.service';
import { generateErrorId } from '../shared/utils/unique-id-utils';
import { OrganizationService } from '../core/services/organization.service';
import { sanitizeReturnUrl } from '../shared/utils/return-url';
import { ThemeService, Theme } from '../core/services/theme.service';

@Component({
    selector: 'juyo-landing-page',
    styleUrl: 'landing.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgOptimizedImage, SignInComponent, SignUpComponent],
    template: `
        <div class="page-body">
            <div class="form-container">
                <div class="panel">
                    <img
                        ngSrc="assets/logos/logo_white.png"
                        width="415"
                        height="129"
                        class="logo"
                        alt="Juyo logo"
                        priority
                    />

                    @if (!isCheckingSession()) {
                        <div class="tab-toggle">
                            <button
                                class="tab-btn"
                                [class.active]="activeTab() === LandingPageType.SignIn"
                                (click)="activeTab.set(LandingPageType.SignIn)"
                            >
                                SIGN IN
                            </button>
                            <button
                                class="tab-btn"
                                [class.active]="activeTab() === LandingPageType.SignUp"
                                (click)="onSignUpTabSelected()"
                            >
                                CREATE ACCOUNT
                            </button>
                        </div>

                        @if (showOrganizationEndpointError()) {
                            <div class="hotel-error">
                                Oops! We couldn't load your organization data. Please try refreshing the page or contact
                                support if the issue persists.
                            </div>
                        }

                        @switch (activeTab()) {
                            @case (LandingPageType.SignIn) {
                                <juyo-sign-in (signedIn)="onSignedIn()" />
                            }
                            @case (LandingPageType.SignUp) {
                                <juyo-sign-up (signedUp)="onSignedUp()" />
                            }
                        }
                    }
                </div>
            </div>

            <footer class="footer">
                <span>© 2026 Juyo Analytics</span>
                <span class="separator">·</span>
                <a target="_blank" href="https://juyo.ai/privacy-policy">Privacy</a>
                <span class="separator">·</span>
                <a target="_blank" href="https://juyo.ai/data-processing-agreement">Data Processing Agreement</a>
            </footer>
        </div>
    `,
})
export class LandingPage implements OnInit {
    public activeTab = signal<LandingPageType>(LandingPageType.SignIn);
    public isCheckingSession = signal(true);
    public showOrganizationEndpointError = signal(false);

    private authenticationService = inject(AuthenticationService);
    private userService = inject(UserService);
    private organizationService = inject(OrganizationService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private clientLoggingService = inject(ClientLoggingService);
    private themeService = inject(ThemeService);

    protected readonly LandingPageType = LandingPageType;

    public onSignUpTabSelected(): void {
        this.activeTab.set(LandingPageType.SignUp);
        this.themeService.setTheme(Theme.Dark);
    }

    public ngOnInit(): void {
        if (this.activatedRoute.snapshot.queryParamMap.get('sign-up') === 'true') {
            this.onSignUpTabSelected();
            this.router.navigate([], {
                queryParams: { 'sign-up': null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        }

        this.authenticationService.isSessionValid().then((isSessionValid) => {
            if (isSessionValid) {
                this.userService.loadUser(true).subscribe({
                    next: (user) => {
                        if (user) {
                            this.navigateAfterSignin();
                        } else {
                            this.isCheckingSession.set(false);
                        }
                    },
                    error: () => this.isCheckingSession.set(false),
                });
            } else {
                this.isCheckingSession.set(false);
            }
        });
    }

    public onSignedIn(): void {
        this.navigateAfterSignin();
    }

    public onSignedUp(): void {
        this.router.navigate([AppUrls.onboardingUrl]);
    }

    private navigateAfterSignin(): void {
        this.organizationService.getOrganizations().subscribe({
            next: (organizations) => {
                if (organizations && organizations.length > 0) {
                    this.showOrganizationEndpointError.set(false);
                    const returnUrl = sanitizeReturnUrl(this.activatedRoute.snapshot.queryParamMap.get('returnUrl'));
                    if (returnUrl) {
                        this.router.navigateByUrl(returnUrl);
                        return;
                    }
                    // Carry the org ( ?chainId from the external platform) over so the
                    // organization guard can select the matching organization.
                    const params = this.activatedRoute.snapshot.queryParamMap;
                    this.router.navigate([AppUrls.hotelGptUrl], {
                        queryParams: { chainId: params.get('chainId'), orgId: params.get('orgId') },
                    });
                } else {
                    this.showOrganizationEndpointError.set(false);
                    this.router.navigate([AppUrls.noAccessUrl]);
                }
            },
            error: (error: unknown) => {
                const correlationId = generateErrorId();
                this.clientLoggingService.log(correlationId, error);
                this.isCheckingSession.set(false);
                this.showOrganizationEndpointError.set(true);
            },
        });
    }
}

enum LandingPageType {
    SignIn = 'signIn',
    SignUp = 'signUp',
}
